import { captureException } from '@sentry/nextjs';
import mixpanel, { type Config } from 'mixpanel-browser';

import { devError, devWarn } from '@components/utils';

type InitConfig = {
    MIXPANEL_API?: string;
    MIXPANEL_ENABLED?: boolean;
    MIXPANEL_TOKEN?: string;
};

// FIXME: SHRP-36437 [FE] Refactor the mixpanel.utils and useMixpanel to use a single state for initialized
let isInited = false;

const initMixpanel = ({
    MIXPANEL_API = undefined,
    MIXPANEL_ENABLED = false,
    MIXPANEL_TOKEN = undefined
}: InitConfig): boolean => {
    if (!MIXPANEL_ENABLED) {
        return false;
    }

    if (!MIXPANEL_TOKEN) {
        devWarn('Mixpanel token is missing! Check your .env file.');
        return false;
    }

    try {
        mixpanel.init(MIXPANEL_TOKEN, {
            api_host: MIXPANEL_API,
            debug: false,
            track_pageview: false,
            autocapture: {
                pageview: false,
                click: false,
                input: false,
                scroll: false,
                submit: false,
                capture_text_content: false
            },
            persistence: 'localStorage',
            // Mask sensitive data from being sent to Mixpanel
            property_blacklist: ['current_page_title', 'current_url_path', '$current_url']
        } as Partial<Config>);
        isInited = true;
    } catch (error) {
        captureException(error);
        devError('Mixpanel init exception');

        return false;
    }

    return true;
};

const identifyUser = (userId: string) => {
    try {
        mixpanel.identify(userId);
    } catch {
        devError('Mixpanel identify exception');
    }
};

const reset = () => {
    try {
        mixpanel.reset();
    } catch {
        devError('Mixpanel reset exception');
    }
};

const setSuperProps = (props: Record<string, string>) => {
    try {
        mixpanel.register(props);
    } catch {
        devError('Mixpanel register exception');
    }
};

const setUserProfile = (props: Record<string, string>) => {
    try {
        mixpanel.people.set(props);
    } catch {
        devError('Mixpanel profile exception');
    }
};

const track = (event: string, props: Record<string, string>) => {
    if (!isInited) return;
    try {
        mixpanel.track(event, props);
    } catch (error) {
        captureException(error, { extra: { event, ...props } });
        devError('Mixpanel track exception');
    }
};

export { identifyUser, initMixpanel, reset, setSuperProps, setUserProfile, track };
